import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { APIURL } from '../../../config';
import moment from 'moment';
import {
    Typography, Button, List, ListItem, ListItemText,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, CircularProgress, Card, Link, Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const emptyForm = () => ({
    vaccineOption: null,
    dateAdministered: moment().format('YYYY-MM-DD'),
    doseNumber: '',
    notes: '',
});

function VaccineDetailModal({ vaccine, open, onClose }) {
    if (!vaccine) return null;
    const infoUrl = vaccine.PdfUrl || vaccine.HtmlUrl;
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{vaccine.DocTypeDesc || '(unnamed vaccine)'}</DialogTitle>
            <DialogContent>
                {vaccine.dateAdministered && (
                    <Typography variant="body2" style={{ marginBottom: 6 }}>
                        <span style={{ color: '#666' }}>Date Administered:</span> {moment(vaccine.dateAdministered).format('MM/DD/YYYY')}
                    </Typography>
                )}
                {vaccine.doseNumber && (
                    <Typography variant="body2" style={{ marginBottom: 6 }}>
                        <span style={{ color: '#666' }}>Dose Number:</span> {vaccine.doseNumber}
                    </Typography>
                )}
                {vaccine.notes && (
                    <Typography variant="body2" style={{ marginBottom: 6 }}>
                        <span style={{ color: '#666' }}>Notes:</span> {vaccine.notes}
                    </Typography>
                )}
                {infoUrl && (
                    <Typography variant="body2" style={{ marginBottom: 6 }}>
                        <span style={{ color: '#666' }}>Vaccine Information Statement (VIS):</span>{' '}
                        <Link href={infoUrl} target="_blank" rel="noopener noreferrer">View</Link>
                    </Typography>
                )}
                {vaccine.addedOn && (
                    <Typography variant="body2" style={{ marginBottom: 6 }}>
                        <span style={{ color: '#666' }}>Added On:</span> {moment(vaccine.addedOn).format('MM/DD/YYYY')}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

function VaccineSection({ vaccines, customer_id, addedBy, onAdd, onDelete }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailVaccine, setDetailVaccine] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [vaccineOptions, setVaccineOptions] = useState([]);
    const [vaccineLoading, setVaccineLoading] = useState(false);
    const debounceRef = useRef(null);

    const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const handleVaccineSearch = useCallback((inputValue) => {
        clearTimeout(debounceRef.current);
        if (!inputValue || inputValue.length < 3) { setVaccineOptions([]); return; }
        debounceRef.current = setTimeout(async () => {
            setVaccineLoading(true);
            try {
                const res = await axios.get(APIURL + `/customer_vaccine/suggestions`, { params: { searchTerm: inputValue } });
                setVaccineOptions(res.data.map(v => ({
                    VISUrlID: v.VISUrlID,
                    label: v.DocTypeDesc,
                    DocTypeDesc: v.DocTypeDesc,
                    PdfUrl: v.PdfUrl,
                    HtmlUrl: v.HtmlUrl,
                })));
            } catch (err) {
                console.error(err);
            }
            setVaccineLoading(false);
        }, 300);
    }, []);

    const validate = () => {
        if (!form.vaccineOption) { alert('Please select a vaccine.'); return false; }
        if (!form.dateAdministered) { alert('Please enter the date administered.'); return false; }
        return true;
    };

    const handleSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const payload = {
                customer_id,
                addedBy,
                VISUrlID: form.vaccineOption?.VISUrlID || null,
                dateAdministered: form.dateAdministered,
                doseNumber: form.doseNumber,
                notes: form.notes,
            };
            const res = await axios.post(APIURL + `/customer_vaccine`, payload);
            onAdd({
                customer_vaccine_id: res.data?.id || Date.now(),
                VISUrlID: payload.VISUrlID,
                DocTypeDesc: form.vaccineOption?.DocTypeDesc,
                PdfUrl: form.vaccineOption?.PdfUrl,
                HtmlUrl: form.vaccineOption?.HtmlUrl,
                dateAdministered: form.dateAdministered,
                doseNumber: form.doseNumber,
                notes: form.notes,
                addedOn: new Date().toISOString(),
            });
            setForm(emptyForm());
            setDialogOpen(false);
        } catch (err) {
            console.error(err);
        }
        setSaving(false);
    };

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        setDeleting(id);
        try {
            await axios.delete(APIURL + `/customer_vaccine/${id}`);
            onDelete(id);
        } catch (err) {
            console.error(err);
        }
        setDeleting(null);
    };

    return (
        <Card elevation={3} style={{ marginBottom: 5, padding: 12, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Typography variant="subtitle1" fontWeight="bold">VACCINES</Typography>
                <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={() => setDialogOpen(true)}>
                    Add
                </Button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
                {vaccines.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">No vaccines recorded.</Typography>
                ) : (
                    <List dense disablePadding style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {vaccines.map((vaccine, i) => (
                            <ListItem
                                key={vaccine.customer_vaccine_id || i}
                                disablePadding
                                onClick={() => setDetailVaccine(vaccine)}
                                sx={{
                                    px: 1,
                                    borderRadius: 1,
                                    border: '1px solid #e8e8e8',
                                    cursor: 'pointer',
                                    bgcolor: '#fafafa',
                                    transition: 'background-color 0.15s ease, border-color 0.15s ease',
                                    '&:hover': { bgcolor: '#EBF5FB', borderColor: '#90CAF9' },
                                }}
                            >
                                <ListItemText
                                    primary={vaccine.DocTypeDesc || '(unnamed vaccine)'}
                                    secondary={vaccine.dateAdministered
                                        ? moment(vaccine.dateAdministered).format('MM/DD/YYYY')
                                        : (vaccine.addedOn ? moment(vaccine.addedOn).format('MM/DD/YYYY') : '')}
                                    primaryTypographyProps={{ fontWeight: 500 }}
                                />
                                <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled', mr: 0.5 }} />
                                <IconButton size="small" onClick={(e) => handleDelete(e, vaccine.customer_vaccine_id)} disabled={deleting === vaccine.customer_vaccine_id}>
                                    {deleting === vaccine.customer_vaccine_id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" color='error' />}
                                </IconButton>
                            </ListItem>
                        ))}
                    </List>
                )}
            </div>

            <VaccineDetailModal vaccine={detailVaccine} open={!!detailVaccine} onClose={() => setDetailVaccine(null)} />

            <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setVaccineOptions([]); }} maxWidth="sm" fullWidth>
                <DialogTitle>Add Vaccine</DialogTitle>
                <DialogContent>
                    {/* Vaccine Name */}
                    <Autocomplete
                        options={vaccineOptions}
                        loading={vaccineLoading}
                        value={form.vaccineOption}
                        onChange={(_, newValue) => set('vaccineOption', newValue)}
                        onInputChange={(_, inputValue) => handleVaccineSearch(inputValue)}
                        getOptionLabel={o => o?.label || ''}
                        isOptionEqualToValue={(o, v) => o.VISUrlID === v.VISUrlID}
                        noOptionsText="Type 3+ characters to search"
                        filterOptions={x => x}
                        size="small"
                        style={{ marginTop: 10 }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Vaccine Name"
                                required
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {vaccineLoading ? <CircularProgress size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    {/* Date Administered */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
                        <Typography variant="body2" style={{ fontWeight: 600, minWidth: 130 }}>Date administered</Typography>
                        <TextField
                            type="date"
                            size="small"
                            variant="outlined"
                            value={form.dateAdministered}
                            onChange={e => set('dateAdministered', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </div>

                    {/* Dose Number */}
                    <TextField
                        label="Dose Number"
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={form.doseNumber}
                        onChange={e => set('doseNumber', e.target.value)}
                        style={{ marginTop: 14 }}
                    />

                    {/* Notes */}
                    <TextField
                        label="Notes"
                        fullWidth
                        size="small"
                        variant="outlined"
                        multiline
                        minRows={2}
                        value={form.notes}
                        onChange={e => set('notes', e.target.value)}
                        style={{ marginTop: 14 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : 'Add Vaccine'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
}

export default VaccineSection;
